import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getSystemInfo } from '@renderer/utils/systemInfo'
import { useSettingsStore } from './settingsStore'
import { useAuthStore } from './authStore'
import { useBoothEventStore } from './boothEventStore'
import { useSubscriptionStore, ValidationErrorReason } from './subscriptionStore'
import {
  clearSubscriptionState as bridgeClearSubscriptionState,
  getSubscriptionState as bridgeGetSubscriptionState,
  saveSubscriptionState as bridgeSaveSubscriptionState,
} from '@renderer/lib/runtime'

// Maximum time a cached subscription is trusted without server verification
const OFFLINE_GRACE_SECONDS = 72 * 3600 // 72 hours

interface ValidateErrorBody {
  valid?: boolean
  error?: string
  /** Machine-stable codes from POST /api/subscription/validate (dreambooth web) */
  reason?: string
  subscription?: {
    type: string
    plan: string | null
    startDate?: string
    endDate?: string
  }
}

interface CreditsState {
  credits: number | string
  package: string
  isLoading: boolean
  error: string | null
  fetchCredits: () => Promise<void>
  fetchSubscriptionAndCredits: () => Promise<void>
}

function isLikelyNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true
  if (error instanceof Error) {
    const m = error.message.toLowerCase()
    return /failed to fetch|network|load failed|aborted|fetch/i.test(m)
  }
  return false
}

/**
 * Maps API `reason` or legacy message to a subscription validation error.
 * Returns null when this response is not a definitive business-rule rejection from validate.
 */
function resolveValidateBusinessReason(
  data: ValidateErrorBody,
  status: number,
  errMsg: string
): ValidationErrorReason | null {
  const code = typeof data.reason === 'string' ? data.reason.trim() : ''
  switch (code) {
    case 'INVALID_TOKEN':
      return 'invalid_token'
    case 'NOT_ACTIVE':
      return 'not_active'
    case 'EXPIRED':
      return 'expired'
    case 'DEVICE_MISMATCH':
      return 'device_mismatch'
    case 'WRONG_ACCOUNT':
      return 'wrong_account'
    default:
      break
  }

  if (status === 403) {
    if (
      errMsg.includes('different account') ||
      errMsg.includes('another account') ||
      errMsg.includes('belongs to')
    ) {
      return 'wrong_account'
    }
    return null
  }

  if (status === 400) {
    if (errMsg === 'Invalid activation token') return 'invalid_token'
    if (errMsg === 'Subscription is not active') return 'not_active'
    if (errMsg === 'Subscription has expired') return 'expired'
    if (errMsg === 'Device not authorized') return 'device_mismatch'
    return 'invalid_token'
  }

  return null
}

// Single-flight guard for fetchSubscriptionAndCredits: concurrent callers
// (View error-recovery effect, Welcome pre-flight, SettingsModal, App bootstrap)
// share the same in-flight request. Two parallel calls used to race on
// applyBusinessVerdictFailure → setActivationCode(''), where the second
// response could clobber the first verdict and put the UI into a mixed state.
let inFlightFetchSubscriptionAndCredits: Promise<void> | null = null

export const useCreditsStore = create<CreditsState>()(
  persist(
    (set) => ({
      credits: 0,
      package: 'free',
      isLoading: false,
      error: null,
      fetchCredits: async (): Promise<void> => {
        console.log('🔵 fetchCredits called')
        try {
          set({ isLoading: true, error: null })
          const url = `${process.env.VITE_API_URL}/api/credits`
          console.log('🔵 Fetching from:', url)

          const response = await fetch(url, {
            credentials: 'include'
          })

          console.log('🔵 Response status:', response.status, response.ok)

          if (!response.ok) {
            throw new Error('Failed to fetch credits')
          }

          const data = await response.json()

          // Log API response data
          console.log('=== Credits API Response ===')
          console.log('Endpoint:', '/api/credits')
          console.log('Response Data:', data)
          console.log('Credits:', data.credits)
          console.log('Package:', data.package)
          console.log('Timestamp:', new Date().toISOString())
          console.log('===========================')

          set({ credits: data.credits, package: data.package })

          // Do not set subscriptionStore.type from account package here — subscription type
          // in the UI is for device token only. When there's no token we show "Upgrade", not
          // the user's web package (they can have multiple subscription types).
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to fetch credits' })
        } finally {
          set({ isLoading: false })
        }
      },
      fetchSubscriptionAndCredits: async (): Promise<void> => {
        if (inFlightFetchSubscriptionAndCredits) {
          console.log('🟢 fetchSubscriptionAndCredits: joining in-flight request')
          return inFlightFetchSubscriptionAndCredits
        }
        const run = doFetchSubscriptionAndCredits()
        inFlightFetchSubscriptionAndCredits = run
        try {
          await run
        } finally {
          if (inFlightFetchSubscriptionAndCredits === run) {
            inFlightFetchSubscriptionAndCredits = null
          }
        }
      }
    }),
    {
      name: 'credits-store',
      partialize: (state) => ({
        credits: state.credits,
        package: state.package
      })
    }
  )
)

async function doFetchSubscriptionAndCredits(): Promise<void> {
        console.log('🟢 fetchSubscriptionAndCredits called')
        const set = (partial: Partial<CreditsState>): void => {
          useCreditsStore.setState(partial)
        }
        const get = (): CreditsState => useCreditsStore.getState()
        set({ isLoading: true, error: null })

        const { activationCode, deviceId } = useSettingsStore.getState()
        const setSubscription = useSubscriptionStore.getState().setSubscription

        console.log('🟢 activationCode:', activationCode ? 'exists' : 'none')
        console.log('🟢 deviceId:', deviceId ? deviceId.substring(0, 20) + '...' : 'none')

        const applyOfflineGraceIfEligible = async (): Promise<boolean> => {
          try {
            const cached = await bridgeGetSubscriptionState()
            const now = Math.floor(Date.now() / 1000)

            if (
              cached &&
              cached.isValid &&
              cached.offlineGraceExpiresAt &&
              cached.offlineGraceExpiresAt > now
            ) {
              const adjustedNow = now + (cached.serverTimeOffset || 0)
              if (!cached.endDate || cached.endDate > adjustedNow) {
                console.log('📦 [SUBSCRIPTION] Using cached subscription (offline grace period)')
                setSubscription({
                  type: cached.type || 'free',
                  plan: cached.plan || null,
                  isValid: true
                })
                set({
                  credits: 'Unlimited',
                  package: cached.type || 'free',
                  error: null
                })
                return true
              }
            }
          } catch (cacheError) {
            console.warn('[SUBSCRIPTION] Failed to check cached state:', cacheError)
          }
          return false
        }

        /**
         * Server rejected the licence/token on a business rule (device mismatch,
         * expired, not-active, invalid, wrong-account). Mark the subscription invalid
         * and drop access + the offline-grace cache, but DO NOT erase the stored
         * activation code. Keeping the token lets the operator recover without
         * re-typing it (e.g. after a transient device-fingerprint change). The token
         * is only removed on a genuine account switch (see authStore).
         */
        const applyBusinessVerdictFailure = async (
          validationError: ValidationErrorReason
        ): Promise<void> => {
          console.warn('[SUBSCRIPTION] Subscription invalid — activation code retained', {
            reason: validationError,
            source: 'validate_business_verdict'
          })
          // Surface the verdict to the dashboard (console.warn never leaves the
          // booth). Coalesced in the store so a persistent verdict isn't spammy.
          useBoothEventStore
            .getState()
            .recordLicenseIncident('invalid_retained', { reason: validationError })
          setSubscription({
            type: 'free',
            plan: null,
            isValid: false,
            proTrialExpiresAt: undefined,
            validationError
          })
          set({ credits: 0, package: 'free' })
          void bridgeClearSubscriptionState()
          console.log('🟢 Calling fetchCredits from fetchSubscriptionAndCredits')
          await get().fetchCredits()
        }

        try {
          if (!activationCode) {
            console.log('🟢 Calling fetchCredits from fetchSubscriptionAndCredits')
            await get().fetchCredits()
            return
          }

          let response: Response
          try {
            response = await fetch(`${process.env.VITE_API_URL}/api/subscription/validate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ token: activationCode, deviceId }),
              credentials: 'include'
            })
          } catch (fetchErr) {
            console.error('Error in fetchSubscriptionAndCredits:', fetchErr)
            if (await applyOfflineGraceIfEligible()) return
            const msg =
              fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch subscription and credits'
            set({ error: msg })
            return
          }

          let data: ValidateErrorBody
          try {
            data = (await response.json()) as ValidateErrorBody
          } catch {
            console.warn('[SUBSCRIPTION] Non-JSON response from validate')
            if (await applyOfflineGraceIfEligible()) return
            set({ error: 'Invalid server response' })
            return
          }

          console.log('=== Subscription Validation API Response ===')
          console.log('Endpoint:', '/api/subscription/validate')
          console.log('Response Data:', data)
          console.log('Valid:', data.valid)
          if (data.subscription) {
            console.log('Subscription Type:', data.subscription.type)
            console.log('Subscription Plan:', data.subscription.plan)
          }
          console.log('Timestamp:', new Date().toISOString())
          console.log('===========================================')

          if (response.ok && data.valid && data.subscription !== undefined) {
            const { computerName, osInfo } = getSystemInfo()
            const endDate = data.subscription?.endDate
              ? new Date(data.subscription.endDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : undefined
            setSubscription({
              type: data.subscription.type,
              plan: data.subscription.plan,
              isValid: true,
              computerName,
              osInfo,
              deviceId,
              activationCode,
              endDate,
              validationError: null
            })
            set({ credits: 'Unlimited', package: data.subscription.type })

            // Record the server-confirmed owner of this activation code so authStore
            // can clear it later ONLY on a genuine account switch (never on logout).
            try {
              const ownerEmail = useAuthStore.getState().user?.email
              if (ownerEmail) useSettingsStore.getState().setLicenseOwnerEmail(ownerEmail)
            } catch {
              // non-fatal — owner tracking is best-effort
            }

            const serverDateHeader = response.headers.get('Date')
            let serverTimeOffset = 0
            if (serverDateHeader) {
              const serverTime = Math.floor(new Date(serverDateHeader).getTime() / 1000)
              const localTime = Math.floor(Date.now() / 1000)
              serverTimeOffset = serverTime - localTime
            }

            try {
              await bridgeSaveSubscriptionState({
                activationCode,
                deviceId,
                type: data.subscription.type,
                plan: data.subscription.plan,
                isValid: 1,
                startDate: data.subscription.startDate
                  ? Math.floor(new Date(data.subscription.startDate).getTime() / 1000)
                  : null,
                endDate: data.subscription.endDate
                  ? Math.floor(new Date(data.subscription.endDate).getTime() / 1000)
                  : null,
                lastVerifiedAt: Math.floor(Date.now() / 1000),
                offlineGraceExpiresAt: Math.floor(Date.now() / 1000) + OFFLINE_GRACE_SECONDS,
                serverTimeOffset
              })
            } catch (error) {
              console.error('Failed to persist subscription state', error)
            }

            return
          }

          const status = response.status
          const errMsg = typeof data.error === 'string' ? data.error : ''

          if (status >= 500 || status === 408 || status === 429) {
            if (await applyOfflineGraceIfEligible()) return
            set({ error: errMsg || 'Server temporarily unavailable' })
            return
          }

          if (status === 401) {
            if (await applyOfflineGraceIfEligible()) return
            setSubscription({
              type: 'free',
              plan: null,
              isValid: false,
              proTrialExpiresAt: undefined,
              validationError: 'session_required'
            })
            set({ credits: 0, package: 'free', error: null })
            console.log('🟢 Calling fetchCredits from fetchSubscriptionAndCredits')
            await get().fetchCredits()
            return
          }

          const businessReason = resolveValidateBusinessReason(data, status, errMsg)
          if (businessReason !== null) {
            await applyBusinessVerdictFailure(businessReason)
            return
          }

          if (await applyOfflineGraceIfEligible()) return
          set({
            error: errMsg || `Subscription validation failed (${status})`
          })
        } catch (error) {
          console.error('Error in fetchSubscriptionAndCredits:', error)
          if (await applyOfflineGraceIfEligible()) return
          const msg =
            error instanceof Error ? error.message : 'Failed to fetch subscription and credits'
          if (isLikelyNetworkError(error)) {
            set({ error: msg })
            return
          }
          set({ error: msg })
        } finally {
          set({ isLoading: false })
        }
}

export const checkCredits = async (): Promise<boolean> => {
  const { fetchSubscriptionAndCredits, credits } = useCreditsStore.getState()
  await fetchSubscriptionAndCredits()
  return typeof credits === 'number' ? credits > 0 : true // Unlimited credits if package is not free
}
